import { DASHBOARD_VIEW_TYPE } from '@adtrackify/at-tracking-event-types';
import {
  BarChart3,
  BookOpen,
  Cog,
  CreditCard,
  ExternalLink,
  KeyRound,
  Gauge,
  LayoutGrid,
  ListChecks,
  LogOut,
  MessageCircle,
  MoreHorizontal,
  Plus,
  Settings,
  TrendingUp,
  type LucideIcon,
} from 'lucide-react';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { useIsClientMounted } from '@/hooks/browser/use-is-client-mounted';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { MobileAppearanceThemeList } from '@/app/shell/appearance-theme-radio-group';
import { MobileAccountSwitcher } from '@/features/workspace/components/team-switcher/mobile-account-switcher';
import { Button } from '@/components/ui/button';
import type { LegacyNavItem,NavStatusSnapshot } from '@/lib/navigation/types/nav-types';
import { DRAWER_APPEARANCE } from '@/components/ui/types/drawer-variants-enums';
import { Drawer, DrawerClose, DrawerContent, DrawerFooter, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { useIsMobile } from '@/hooks/browser/use-is-mobile';
import {
  NAV_STATUS_KEY,
  NAV_STATUS_VARIANT,
} from '@/lib/navigation/types/nav-enums';
import {
  REPORTS_SECTION_TITLE,
  SYSTEM_SECTION_TITLE,
} from '@/lib/navigation/constants/navigation-constants';

import {
  isAnyNavLinkActive,
  isNavLinkActive,
} from '@/lib/navigation/nav-match';
import {
  getDesktopNonDashboardItems,
  getMobileReportsItems,
  getMobileSystemItems,
  toLegacyNavItems,
} from '@/lib/navigation/nav-presenters';
import {
  aggregateControlPanelStatus,
  formatUnreadCountLabel,
  getStatusAccessibleLabel,
} from '@/features/workspace/helpers/nav-status';
import { useNavStatusSnapshot } from '@/app/shell/hooks/use-nav-status-snapshot';
import {
  getActionsForSurface,
} from '@/lib/navigation/account-action-catalog';
import type {
  AccountActionDefinition,
} from '@/lib/navigation/types/account-action-catalog-types';
import {
  AccountActionId,
  AccountActionKind,
  AccountMenuSurface,
} from '@/lib/navigation/types/account-action-catalog-enums';
import {
  SIGN_OUT_LABEL,
} from '@/lib/navigation/constants/account-action-catalog-constants';
import { useAccountSignOut } from '@/features/auth/hooks/use-account-sign-out';
import { useResolvedAccountActionCatalog } from '@/hooks/navigation/use-resolved-account-action-catalog';
import { getSidebarNavLeafItemClassName, sidebarSectionHeading, sidebarStatusIndicator } from '@/components/ui/sidebar/helpers/sidebar-theme';
import { SIDEBAR_NAV_ITEM_LAYOUT, SIDEBAR_NAV_ITEM_STATE } from '@/components/ui/sidebar/types/sidebar-theme-enums';
import { cn } from '@/lib/utils';
import { selectViewIdForPixel, useDashboardSelectionStore } from '@/features/dashboard/stores/use-dashboard-selection-store';
import { useDashboardUIStore } from '@/features/dashboard/stores/dashboard-ui-store';
import { useDashboardBootstrap } from '@/features/dashboard/hooks/use-dashboard-bootstrap';
import { resolveDashboardViewLabel } from '@/features/dashboard/helpers/view-helpers';
import { SUMMARY_VIEW_NAME } from '@/features/dashboard/constants/view-name-constants';
import { openNamedDashboardView } from '@/features/dashboard/helpers/pending-view-selection';
import { useCurrentAccount, usePixelId } from '@/contexts/account/account-context';
import { useHasShopifyData } from '@/features/shopify/hooks/use-has-shopify-data';
import { useIntercom } from '@/contexts/intercom/intercom-context';
import useStore from '@/store/app/use-store';

// ---------------------------------------------------------------------------
// Routes
// ---------------------------------------------------------------------------

const ROUTE_VIEWS = '/board/views';

function useMobileSummaryDestination() {
  const location = useLocation();
  const pixelId = usePixelId();
  const { data: bootstrapData } = useDashboardBootstrap(pixelId);
  const selectedViewId = useDashboardSelectionStore(state =>
    selectViewIdForPixel(state, pixelId)
  );
  const setSelectedViewId = (viewId: string | null) => {
    useDashboardSelectionStore.getState().setSelectedViewId(viewId, pixelId ?? undefined);
  };
  const views = bootstrapData?.viewsByType?.[DASHBOARD_VIEW_TYPE.VIEW];
  const summaryView = views?.find(view => view.name === SUMMARY_VIEW_NAME);
  const isOnOverview = location.pathname === ROUTE_VIEWS;
  const isSummaryActive = Boolean(summaryView && isOnOverview && selectedViewId === summaryView.id);

  const openSummary = () => {
    openNamedDashboardView(views, SUMMARY_VIEW_NAME, setSelectedViewId);
  };

  return { isOnOverview, isSummaryActive, openSummary };
}

function useReportsSheetItems(): LegacyNavItem[] {
  const { hasShopifyData } = useHasShopifyData();
  return useMemo(
    () =>
      toLegacyNavItems(
        getMobileReportsItems({
          hasShopifyData,
          accountFeatureFlagNames: [],
        })
      ),
    [hasShopifyData]
  );
}

function useSystemSheetItems(): LegacyNavItem[] {
  const { hasShopifyData } = useHasShopifyData();
  return useMemo(
    () =>
      toLegacyNavItems(
        getMobileSystemItems({
          hasShopifyData,
          accountFeatureFlagNames: [],
        })
      ),
    [hasShopifyData]
  );
}

// ---------------------------------------------------------------------------
// Status helpers (desktop AppSidebar parity)
// ---------------------------------------------------------------------------

function getStatusIndicatorClassName(variant: NAV_STATUS_VARIANT): string {
  switch (variant) {
    case NAV_STATUS_VARIANT.SUCCESS:
      return sidebarStatusIndicator({ status: 'success' });
    case NAV_STATUS_VARIANT.WARNING:
      return sidebarStatusIndicator({ status: 'warning' });
    case NAV_STATUS_VARIANT.ERROR:
      return sidebarStatusIndicator({ status: 'error' });
    case NAV_STATUS_VARIANT.PENDING:
      return sidebarStatusIndicator({ status: 'pending' });
  }
}

function resolveItemStatus(
  item: LegacyNavItem,
  statusSnapshot?: NavStatusSnapshot
): NAV_STATUS_VARIANT | undefined {
  if (!item.hasStatus || !statusSnapshot) return undefined;

  const key = item.statusKey;
  if (key) return statusSnapshot.variants[key];

  if (item.name === 'Data Sources') return statusSnapshot.variants[NAV_STATUS_KEY.INTEGRATION_HEALTH];
  if (item.name === 'Destinations') return statusSnapshot.variants[NAV_STATUS_KEY.DESTINATION_HEALTH];
  if (item.name === 'Events Manager') {
    return statusSnapshot.variants[NAV_STATUS_KEY.EVENT_HEALTH];
  }
  if (item.name === 'Meta EMQ') {
    return statusSnapshot.variants[NAV_STATUS_KEY.EMQ_HEALTH];
  }

  return NAV_STATUS_VARIANT.SUCCESS;
}

function StatusDot({ variant }: { variant: NAV_STATUS_VARIANT }) {
  const label = getStatusAccessibleLabel(variant);

  return (
    <span className="flex items-center gap-1">
      <span className={cn('h-2 w-2 shrink-0', getStatusIndicatorClassName(variant))} />
      <span className="sr-only">{label}</span>
    </span>
  );
}

function NotificationsBadge({ count }: { count: number }) {
  if (count <= 0) return null;

  const label = formatUnreadCountLabel(count);

  return (
    <span
      aria-label={label}
      className="flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-action-primary px-1 type-settings-row-meta font-medium leading-none tabular-nums text-action-primary-foreground"
    >
      {count > 9 ? '9+' : count}
    </span>
  );
}

function NavItemAccessory({
  item,
  statusSnapshot,
}: {
  item: LegacyNavItem;
  statusSnapshot?: NavStatusSnapshot;
}) {
  const isNotifications =
    item.name === 'Notifications' || item.statusKey === NAV_STATUS_KEY.UNREAD_NOTIFICATIONS;
  const unreadCount = statusSnapshot?.unreadCount ?? 0;

  if (isNotifications) {
    return <NotificationsBadge count={unreadCount} />;
  }

  const statusVariant = resolveItemStatus(item, statusSnapshot);
  if (!statusVariant) return null;

  return <StatusDot variant={statusVariant} />;
}

// ---------------------------------------------------------------------------
// Tab button
// ---------------------------------------------------------------------------

interface TabButtonProps {
  label: string;
  icon: LucideIcon;
  isActive: boolean;
  statusVariant?: NAV_STATUS_VARIANT;
  isSheetOpen?: boolean;
  onClick?: () => void;
}

function TabButton({
  label,
  icon: Icon,
  isActive,
  statusVariant,
  isSheetOpen,
  onClick,
}: TabButtonProps) {
  const showStatusDot =
    statusVariant !== undefined &&
    statusVariant !== NAV_STATUS_VARIANT.SUCCESS &&
    statusVariant !== NAV_STATUS_VARIANT.PENDING;
  const hasSheet = isSheetOpen !== undefined;

  return (
    <button
      aria-current={isActive ? 'page' : undefined}
      aria-expanded={hasSheet ? isSheetOpen : undefined}
      aria-haspopup={hasSheet ? 'dialog' : undefined}
      aria-label={label}
      className={cn(
        'relative flex h-full w-full flex-col items-center justify-center gap-0.5',
        'type-nav font-medium leading-none',
        'transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
        isActive
          ? 'text-interaction-selected-indicator'
          : 'text-content-tertiary hover:text-content-primary'
      )}
      onClick={onClick}
      type="button"
    >
      <span className="relative flex items-center justify-center">
        <Icon className="h-5 w-5" strokeWidth={isActive ? 2.25 : 1.75} />
        {showStatusDot ? (
          <span
            aria-hidden="true"
            className={cn(
              'absolute -right-1 -top-0.5 h-2 w-2 rounded-full ring-2 ring-surface-navigation',
              statusVariant === NAV_STATUS_VARIANT.ERROR
                ? 'bg-destructive'
                : 'bg-status-warning'
            )}
          />
        ) : null}
        {statusVariant ? (
          <span className="sr-only">{getStatusAccessibleLabel(statusVariant)}</span>
        ) : null}
      </span>
      <span className="mt-0.5 truncate">{label}</span>
      {/* One selection rail for active tabs — open state is aria-expanded only. */}
      {isActive ? (
        <span
          aria-hidden="true"
          className="absolute inset-x-3 top-0 h-0.5 rounded-b-full bg-interaction-selected-indicator"
        />
      ) : null}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Navigation row primitive (shared across sheets)
// ---------------------------------------------------------------------------

interface NavItemRowProps {
  item: LegacyNavItem;
  isActive: boolean;
  onNavigate: () => void;
  statusSnapshot?: NavStatusSnapshot;
}

function NavItemRow({ item, isActive, onNavigate, statusSnapshot }: NavItemRowProps) {
  if (!item.link) return null;

  return (
    <Link
      aria-current={isActive ? 'page' : undefined}
      className={getSidebarNavLeafItemClassName({
        layout: SIDEBAR_NAV_ITEM_LAYOUT.SHEET,
        state: isActive ? SIDEBAR_NAV_ITEM_STATE.ACTIVE : SIDEBAR_NAV_ITEM_STATE.DEFAULT,
      })}
      onClick={onNavigate}
      to={item.link}
    >
      <span className="min-w-0 flex-1 truncate text-left">{item.name}</span>
      <NavItemAccessory item={item} statusSnapshot={statusSnapshot} />
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Sheet content: Views (custom views)
// ---------------------------------------------------------------------------

function ViewsSheetContent({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const pixelId = usePixelId();
  const { data: bootstrapData, isLoading } = useDashboardBootstrap(pixelId);
  const views = bootstrapData?.viewsByType?.[DASHBOARD_VIEW_TYPE.VIEW] ?? [];

  const selectedViewId = useDashboardSelectionStore(s => selectViewIdForPixel(s, pixelId));
  const setSelectedViewId = (viewId: string | null) => {
    useDashboardSelectionStore.getState().setSelectedViewId(viewId, pixelId ?? undefined);
  };
  const openCreateViewDialog = useDashboardUIStore(s => s.openCreateViewDialog);

  const handleViewClick = useCallback(
    (viewId: string) => {
      setSelectedViewId(viewId);
      void navigate(ROUTE_VIEWS);
      onClose();
    },
    [setSelectedViewId, navigate, onClose]
  );

  const handleCreateView = useCallback(() => {
    void navigate(ROUTE_VIEWS);
    // Defer until after navigation so the dialog mounts on the right page.
    setTimeout(() => {
      openCreateViewDialog();
      onClose();
    }, 100);
  }, [navigate, openCreateViewDialog, onClose]);

  return (
    <div className="flex flex-col gap-2 px-4 pb-2">
      {/* DrawerTitle owns "Views"; keep New view CTA without a competing in-sheet heading */}
      <div className="flex items-center justify-end gap-2">
        <Button
          className="min-h-11 gap-1.5 px-3"
          onClick={handleCreateView}
          size="xs"
          variant="ghost"
        >
          <Plus className="h-4 w-4" />
          New view
        </Button>
      </div>

      {isLoading ? (
        <p className="px-3 py-6 text-center text-sm text-content-tertiary">Loading…</p>
      ) : views.length === 0 ? (
        <p className="px-3 py-6 text-center text-sm text-content-tertiary">
          No views yet. Tap “New view” to create one.
        </p>
      ) : (
        <ul className="flex flex-col gap-1">
          {views.map(view => {
            const isActive = selectedViewId === view.id;
            return (
              <li key={view.id}>
                <button
                  className={getSidebarNavLeafItemClassName({
                    layout: SIDEBAR_NAV_ITEM_LAYOUT.SHEET,
                    state: isActive
                      ? SIDEBAR_NAV_ITEM_STATE.ACTIVE
                      : SIDEBAR_NAV_ITEM_STATE.DEFAULT,
                  })}
                  onClick={() => handleViewClick(view.id)}
                  type="button"
                >
                  {resolveDashboardViewLabel(view)}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sheet content: Reports / System (NavItem lists)
// ---------------------------------------------------------------------------

interface NavItemSheetContentProps {
  items: LegacyNavItem[];
  currentPath: string;
  onClose: () => void;
  statusSnapshot?: NavStatusSnapshot;
}

function NavItemSheetContent({
  items,
  currentPath,
  onClose,
  statusSnapshot,
}: NavItemSheetContentProps) {
  return (
    <div className="flex flex-col gap-3 px-4 pb-2">
      {items.map(item => {
        if (item.children && item.children.length > 0) {
          return (
            <NavItemSection
              currentPath={currentPath}
              item={item}
              key={item.link ?? item.name}
              onNavigate={onClose}
              statusSnapshot={statusSnapshot}
            />
          );
        }

        return (
          <NavItemRow
            isActive={item.link ? isNavLinkActive(currentPath, item.link) : false}
            item={item}
            key={item.link ?? item.name}
            onNavigate={onClose}
            statusSnapshot={statusSnapshot}
          />
        );
      })}
    </div>
  );
}

interface NavItemSectionProps {
  item: LegacyNavItem;
  currentPath: string;
  onNavigate: () => void;
  statusSnapshot?: NavStatusSnapshot;
}

function NavItemSection({
  item,
  currentPath,
  onNavigate,
  statusSnapshot,
}: NavItemSectionProps) {
  const visibleChildren = (item.children ?? []).filter(child => child.link !== item.link);

  return (
    <div className="flex flex-col gap-1.5">
      {item.link ? (
        <NavItemRow
          isActive={isNavLinkActive(currentPath, item.link)}
          item={item}
          onNavigate={onNavigate}
          statusSnapshot={statusSnapshot}
        />
      ) : (
        <span className={sidebarSectionHeading()}>{item.name}</span>
      )}
      {visibleChildren.length > 0 ? (
        <ul className="flex flex-col gap-1.5">
          {visibleChildren.map(child => (
            <li key={child.link ?? child.name}>
              <NavItemRow
                isActive={child.link ? isNavLinkActive(currentPath, child.link) : false}
                item={child}
                onNavigate={onNavigate}
                statusSnapshot={statusSnapshot}
              />
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sheet content: More
// ---------------------------------------------------------------------------

const MORE_ACTION_ICONS: Partial<Record<AccountActionId, LucideIcon>> = {
  [AccountActionId.WORKSPACE_SETTINGS]: Cog,
  [AccountActionId.BILLING]: CreditCard,
  [AccountActionId.ONBOARDING_SETUP]: ListChecks,
  [AccountActionId.DOCUMENTATION]: BookOpen,
  [AccountActionId.LIVE_CHAT]: MessageCircle,
  [AccountActionId.API_KEYS]: KeyRound,
  [AccountActionId.SIGN_OUT]: LogOut,
};

const MORE_SHEET_ROW_CLASS = getSidebarNavLeafItemClassName({
  layout: SIDEBAR_NAV_ITEM_LAYOUT.SHEET,
  state: SIDEBAR_NAV_ITEM_STATE.DEFAULT,
});

export function MobileAcquisitionMoreLinks({ onNavigate }: { onNavigate: () => void }) {
  const currentAccount = useCurrentAccount();
  const { hasShopifyData } = useHasShopifyData();
  const accountFeatureFlagNames =
    currentAccount?.featureFlags?.map(flag => flag.name) ?? [];
  const items = toLegacyNavItems(
    getDesktopNonDashboardItems({
      hasShopifyData,
      accountFeatureFlagNames,
    })
  );

  if (items.length === 0) {
    return null;
  }

  return (
    <>
      {items.map(item => (
        <MoreLinkItem
          key={item.link}
          icon={TrendingUp}
          label={item.name}
          onNavigate={onNavigate}
          to={item.link ?? '#'}
        />
      ))}
    </>
  );
}

function collectMoreCatalogHrefs(
  actions: readonly AccountActionDefinition[],
  acquisitionLinks: readonly LegacyNavItem[]
): string[] {
  const hrefs: string[] = [];
  for (const action of actions) {
    if (action.href) hrefs.push(action.href);
  }
  for (const item of acquisitionLinks) {
    if (item.link) hrefs.push(item.link);
    for (const child of item.children ?? []) {
      if (child.link) hrefs.push(child.link);
    }
  }
  return hrefs;
}

function MoreSheetContent({ onClose }: { onClose: () => void }) {
  const storedUser = useStore(s => s.user);
  const catalog = useResolvedAccountActionCatalog();
  const moreActions = getActionsForSurface(catalog, AccountMenuSurface.MOBILE_MORE);
  const listActions = moreActions.filter(
    a => a.id !== AccountActionId.APPEARANCE && a.id !== AccountActionId.SIGN_OUT
  );
  const hasAppearance = moreActions.some(a => a.id === AccountActionId.APPEARANCE);
  const hasSignOut = moreActions.some(a => a.id === AccountActionId.SIGN_OUT);

  const { show: showIntercom } = useIntercom();
  const { signOut, isSigningOut } = useAccountSignOut({ onBeforeSignOut: onClose });

  const handleSupport = useCallback(() => {
    onClose();
    showIntercom();
  }, [onClose, showIntercom]);

  const email = storedUser?.email;
  const displayName = storedUser?.name?.trim() || email || 'Signed in';

  return (
    <div className="flex flex-col gap-4 px-4 pb-4">
      {/* Single identity / switcher owner */}
      <div className="rounded-lg bg-surface-base px-3 py-3 shadow-surface">
        <p className="truncate text-sm font-medium text-content-primary">{displayName}</p>
        {email && displayName !== email ? (
          <p className="truncate type-settings-row-meta text-content-secondary" title={email}>
            {email}
          </p>
        ) : null}
        <div className="mt-3">
          <MobileAccountSwitcher onAccountSwitched={onClose} />
        </div>
      </div>

      {/* Flat MOBILE_MORE catalog list */}
      <ul className="flex flex-col gap-1">
        {listActions.map(action => (
          <MoreCatalogActionItem
            key={action.id}
            action={action}
            onAction={handleSupport}
            onNavigate={onClose}
          />
        ))}
        <MobileAcquisitionMoreLinks onNavigate={onClose} />
      </ul>

      {hasAppearance ? (
        <div>
          <span className={cn(sidebarSectionHeading(), 'px-3')}>Appearance</span>
          <MobileAppearanceThemeList />
        </div>
      ) : null}

      {hasSignOut ? (
        <Button
          className="h-11 w-full justify-start gap-3 text-destructive hover:bg-destructive/5 hover:text-destructive"
          disabled={isSigningOut}
          onClick={() => {
            void signOut();
          }}
          variant="outline"
        >
          <LogOut className="h-4 w-4" />
          {isSigningOut ? 'Signing out…' : SIGN_OUT_LABEL}
        </Button>
      ) : null}
    </div>
  );
}

interface MoreCatalogActionItemProps {
  action: AccountActionDefinition;
  onNavigate: () => void;
  onAction: () => void;
}

function MoreCatalogActionItem({
  action,
  onNavigate,
  onAction,
}: MoreCatalogActionItemProps) {
  const Icon = MORE_ACTION_ICONS[action.id] ?? Cog;

  if (action.id === AccountActionId.LIVE_CHAT) {
    return (
      <li>
        <button className={MORE_SHEET_ROW_CLASS} onClick={onAction} type="button">
          <Icon className="h-4 w-4 shrink-0 text-content-secondary" />
          {action.label}
        </button>
      </li>
    );
  }

  if (action.kind === AccountActionKind.EXTERNAL_LINK && action.href) {
    return (
      <li>
        <a
          className={MORE_SHEET_ROW_CLASS}
          href={action.href}
          onClick={onNavigate}
          rel="noopener noreferrer"
          target="_blank"
        >
          <Icon className="h-4 w-4 shrink-0 text-content-secondary" />
          {action.label}
          <ExternalLink className="ml-auto h-3 w-3 text-content-tertiary" />
        </a>
      </li>
    );
  }

  if (action.href) {
    return (
      <MoreLinkItem
        icon={Icon}
        label={action.label}
        onNavigate={onNavigate}
        to={action.href}
      />
    );
  }

  return null;
}

interface MoreLinkItemProps {
  icon: LucideIcon;
  label: string;
  to: string;
  onNavigate: () => void;
}

function MoreLinkItem({ icon: Icon, label, to, onNavigate }: MoreLinkItemProps) {
  return (
    <li>
      <Link className={MORE_SHEET_ROW_CLASS} onClick={onNavigate} to={to}>
        <Icon className="h-4 w-4 shrink-0 text-content-secondary" />
        {label}
      </Link>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Status aggregation for System tab
// ---------------------------------------------------------------------------

function getSystemStatusVariant(snapshot: NavStatusSnapshot): NAV_STATUS_VARIANT {
  return aggregateControlPanelStatus(snapshot);
}

// ---------------------------------------------------------------------------
// BottomNavbar
// ---------------------------------------------------------------------------

enum SheetId {
  VIEWS = 'views',
  REPORTS = 'reports',
  SYSTEM = 'system',
  MORE = 'more',
}

export function BottomNavbar() {
  const isMobile = useIsMobile();
  const location = useLocation();
  const [openSheet, setOpenSheet] = useState<SheetId | null>(null);
  const isMounted = useIsClientMounted();
  const reportsSheetItems = useReportsSheetItems();
  const systemSheetItems = useSystemSheetItems();
  const statusSnapshot = useNavStatusSnapshot();
  const systemStatus = getSystemStatusVariant(statusSnapshot);
  const catalog = useResolvedAccountActionCatalog();
  const moreActions = getActionsForSurface(catalog, AccountMenuSurface.MOBILE_MORE);
  const currentAccount = useCurrentAccount();
  const { hasShopifyData } = useHasShopifyData();
  const accountFeatureFlagNames = useMemo(
    () => currentAccount?.featureFlags?.map(flag => flag.name) ?? [],
    [currentAccount?.featureFlags]
  );
  const acquisitionItems = useMemo(
    () =>
      toLegacyNavItems(
        getDesktopNonDashboardItems({
          hasShopifyData,
          accountFeatureFlagNames,
        })
      ),
    [accountFeatureFlagNames, hasShopifyData]
  );

  // Close any open sheet on route change.
  useEffect(() => {
    setOpenSheet(null);
  }, [location.pathname]);

  const currentPath = location.pathname;
  const { isOnOverview, isSummaryActive, openSummary } = useMobileSummaryDestination();
  const isOnViewsHub = isOnOverview && !isSummaryActive;
  const isOnAnyReport = isAnyNavLinkActive(currentPath, reportsSheetItems);
  const isOnSystem = isAnyNavLinkActive(currentPath, systemSheetItems);
  const moreCatalogHrefs = useMemo(
    () => collectMoreCatalogHrefs(moreActions, acquisitionItems),
    [moreActions, acquisitionItems]
  );
  const isOnMoreCatalog = moreCatalogHrefs.some(href => isNavLinkActive(currentPath, href));

  if (!isMounted || !isMobile) return null;

  return (
    <nav
      aria-label="Primary"
      className="fixed inset-x-0 bottom-0 z-chrome border-t border-border bg-surface-navigation pb-[env(safe-area-inset-bottom)] md:hidden"
    >
      <div className="grid h-16 grid-cols-5">
        <DirectTab
          href={ROUTE_VIEWS}
          icon={Gauge}
          isActive={isSummaryActive}
          label={SUMMARY_VIEW_NAME}
          onClick={openSummary}
        />

        <SheetTab
          icon={BarChart3}
          isActive={isOnAnyReport}
          label={REPORTS_SECTION_TITLE}
          openSheet={openSheet}
          renderContent={onClose => (
            <NavItemSheetContent
              currentPath={currentPath}
              items={reportsSheetItems}
              onClose={onClose}
            />
          )}
          setOpenSheet={setOpenSheet}
          sheetId={SheetId.REPORTS}
          title={REPORTS_SECTION_TITLE}
        />

        <SheetTab
          icon={LayoutGrid}
          isActive={isOnViewsHub}
          label="Views"
          openSheet={openSheet}
          renderContent={onClose => <ViewsSheetContent onClose={onClose} />}
          setOpenSheet={setOpenSheet}
          sheetId={SheetId.VIEWS}
          title="Views"
        />

        <SheetTab
          icon={Settings}
          isActive={isOnSystem}
          label={SYSTEM_SECTION_TITLE}
          openSheet={openSheet}
          renderContent={onClose => (
            <NavItemSheetContent
              currentPath={currentPath}
              items={systemSheetItems}
              onClose={onClose}
              statusSnapshot={statusSnapshot}
            />
          )}
          setOpenSheet={setOpenSheet}
          sheetId={SheetId.SYSTEM}
          statusVariant={systemStatus}
          title={SYSTEM_SECTION_TITLE}
        />

        <Drawer
          onOpenChange={open => setOpenSheet(open ? SheetId.MORE : null)}
          open={openSheet === SheetId.MORE}
          shouldScaleBackground
        >
          <TabButton
            icon={MoreHorizontal}
            isActive={isOnMoreCatalog}
            isSheetOpen={openSheet === SheetId.MORE}
            label="More"
            onClick={() =>
              setOpenSheet(openSheet === SheetId.MORE ? null : SheetId.MORE)
            }
          />
          <DrawerContent appearance={DRAWER_APPEARANCE.FOUNDATION} className="max-h-[85vh]">
            <DrawerHeader>
              <DrawerTitle>More</DrawerTitle>
            </DrawerHeader>
            <div className="overflow-y-auto">
              <MoreSheetContent onClose={() => setOpenSheet(null)} />
            </div>
            <DrawerFooter className="pt-2">
              <DrawerClose asChild>
                <Button className="w-full" variant="outline">
                  Close
                </Button>
              </DrawerClose>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      </div>
    </nav>
  );
}

// ---------------------------------------------------------------------------
// Tab variants
// ---------------------------------------------------------------------------

interface SheetTabProps {
  sheetId: SheetId;
  label: string;
  icon: LucideIcon;
  isActive: boolean;
  statusVariant?: NAV_STATUS_VARIANT;
  openSheet: SheetId | null;
  setOpenSheet: (id: SheetId | null) => void;
  renderContent: (onClose: () => void) => ReactNode;
  title: string;
}

function SheetTab({
  sheetId,
  label,
  icon,
  isActive,
  statusVariant,
  openSheet,
  setOpenSheet,
  renderContent,
  title,
}: SheetTabProps) {
  const isOpen = openSheet === sheetId;

  // Every tap toggles this tab's sheet — no auto-navigation, no hidden
  // tap-active-only expand. Tapping a different tab while a sheet is open
  // works because Drawer's `onOpenChange` fires `false` for the previous
  // sheet before this `setOpenSheet` resolves to the new sheetId.
  const handleClick = useCallback(() => {
    setOpenSheet(isOpen ? null : sheetId);
  }, [isOpen, sheetId, setOpenSheet]);

  return (
    <Drawer
      onOpenChange={open => setOpenSheet(open ? sheetId : null)}
      open={isOpen}
      shouldScaleBackground
    >
      <TabButton
        icon={icon}
        isActive={isActive}
        isSheetOpen={isOpen}
        label={label}
        onClick={handleClick}
        statusVariant={statusVariant}
      />
      <DrawerContent appearance={DRAWER_APPEARANCE.FOUNDATION} className="max-h-[80vh]">
        <DrawerHeader>
          <DrawerTitle>{title}</DrawerTitle>
        </DrawerHeader>
        <div className="overflow-y-auto">{renderContent(() => setOpenSheet(null))}</div>
        <DrawerFooter className="pt-2">
          <DrawerClose asChild>
            <Button className="w-full" variant="outline">
              Close
            </Button>
          </DrawerClose>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}

interface DirectTabProps {
  label: string;
  icon: LucideIcon;
  href: string;
  isActive: boolean;
  onClick?: () => void;
}

function DirectTab({ label, icon: Icon, href, isActive, onClick }: DirectTabProps) {
  return (
    <Link
      aria-current={isActive ? 'page' : undefined}
      aria-label={label}
      className={cn(
        'relative flex h-full w-full flex-col items-center justify-center gap-0.5',
        'type-nav font-medium leading-none',
        'transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
        isActive
          ? 'text-interaction-selected-indicator'
          : 'text-content-tertiary hover:text-content-primary'
      )}
      onClick={onClick}
      to={href}
    >
      <span className="relative flex items-center justify-center">
        <Icon className="h-5 w-5" strokeWidth={isActive ? 2.25 : 1.75} />
      </span>
      <span className="mt-0.5 truncate">{label}</span>
      {isActive ? (
        <span
          aria-hidden="true"
          className="absolute inset-x-3 top-0 h-0.5 rounded-b-full bg-interaction-selected-indicator"
        />
      ) : null}
    </Link>
  );
}
